/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

import React from "react";
import { DeviceDetailsProps } from "../types";
import AuDeviceDetails from "./AuDeviceDetails";

/**
 * Normalizes MA6200-G2 A3 controller attributes to match the format expected by AuDeviceDetails.
 *
 * Key differences between A3 (G2) and A2 (G1) attribute formats:
 *  - G2 uses "inputModuleN" for RfSignalActive and UlAttenuation (vs "radioModuleN" in G1)
 *  - G2 optical port power values are stored in dBm directly; AuDeviceDetails divides by 100
 *  - G2 uses "overTemperatureAlarm" (capital T) vs "overtemperatureAlarm" in AuDeviceDetails
 */
const normalizeA3Attributes = (parsedAttributes: any): any => {
  if (!parsedAttributes) return {};

  // Add Param suffix to all nested-object attribute sections
  const combiners = parsedAttributes.combiners || {};
  const systemInfo = parsedAttributes.systemInfo || {};
  const bandConfiguration = parsedAttributes.bandConfiguration || {};

  // Remap inputModuleN → radioModuleN for the two keys AuDeviceDetails reads from systemInfo
  for (let n = 1; n <= 8; n++) {
    const rfKey = `inputModule${n}RfSignalActive`;
    if (rfKey in systemInfo) {
      systemInfo[`radioModule${n}RfSignalActive`] = systemInfo[rfKey];
    }
    const ulAttKey = `inputModule${n}UlAttenuation`;
    if (ulAttKey in systemInfo) {
      systemInfo[`radioModule${n}UlAttenuation`] = systemInfo[ulAttKey];
    }
  }

  // Optical ports: G2 stores values in dBm directly; AuDeviceDetails divides by 100 so scale up
  const lanConnectivity = parsedAttributes.lanConnectivity || {};  
  for (let n = 1; n <= 16; n++) {
    for (const dir of ["RX", "TX"]) {
      const key = `op${n}Transceiver${dir}power`;
      if (key in lanConnectivity) {
        const val = parseFloat(lanConnectivity[key]);
        if (!isNaN(val) && val !== 0) {
          lanConnectivity[key] = String(val * 100);
        }
      }
    }
  }

  // Normalize alarm name: A3 → "overTemperatureAlarm" (capital T), AuDeviceDetails → "overtemperatureAlarm"
  const rawAlarms = parsedAttributes.alarms;
  let alarms = rawAlarms;
  if (rawAlarms && typeof rawAlarms === "object" && !Array.isArray(rawAlarms)) {
    alarms = { ...rawAlarms };
    if ("overTemperatureAlarm" in alarms && !("overtemperatureAlarm" in alarms)) {
      alarms["overtemperatureAlarm"] = alarms["overTemperatureAlarm"];
    }
  }

  return {
    ...parsedAttributes,
    combiners,
    systemInfo,
    lanConnectivity,
    bandConfiguration,
    alarms,
  };
};

const A3DeviceDetails: React.FC<DeviceDetailsProps> = (props) => {
  const normalizedParsedAttributes = React.useMemo(
    () => normalizeA3Attributes(props.parsedAttributes),
    [props.parsedAttributes]
  );

  return <AuDeviceDetails {...props} parsedAttributes={normalizedParsedAttributes} />;
};

export default A3DeviceDetails;
